<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <!-- Header -->
    <div class="offcanvas-header py-3 px-4" style="background: linear-gradient(135deg, #4a6cf7 0%, #6366f1 100%);">
      <div class="d-flex align-items-center">
        <div class="rounded-circle d-flex align-items-center justify-content-center me-3"
          style="width: 36px; height: 36px; background: rgba(255,255,255,0.2);">
          <i class="fa-solid fa-file-invoice text-white" />
        </div>
        <div>
          <h6 class="mb-0 text-white fw-semibold">
            {{ type === 'CREATE'? $t('Create Goods Receipt'): $t('Edit Goods Receipt') }}
          </h6>
          <small class="text-white-50" v-if="type === 'CREATE'">
            {{ $t('Transaction Date') }}: {{ form.transaction_date }}
          </small>
        </div>
      </div>
      <button
        type="button"
        class="btn-close btn-close-white shadow-none"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <!-- Loading -->
    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>

    <!-- Body -->
    <div class="offcanvas-body p-0" style="background-color: #f8f9fc;" v-else>

      <!-- Tax Policy Banner -->
      <div
        class="d-flex align-items-center px-4 py-2"
        :style="{
          background: form.tax_policy === 'no_tax' ? '#f1f3f5'
            : form.tax_policy === 'included' ? '#e8f4fd'
            : '#fff8e1',
          borderBottom: '1px solid ' + (form.tax_policy === 'no_tax' ? '#dee2e6'
            : form.tax_policy === 'included' ? '#b6d4fe'
            : '#ffe082')
        }"
      >
        <i class="fa-solid fa-receipt me-2"
          :class="{
            'text-secondary': form.tax_policy === 'no_tax',
            'text-info': form.tax_policy === 'included',
            'text-warning': form.tax_policy === 'excluded'
          }"
        />
        <span style="font-size: 13px; font-weight: 500;">
          {{ $t('Tax Policy') }}:
        </span>
        <span
          class="badge ms-2"
          :class="{
            'bg-secondary': form.tax_policy === 'no_tax',
            'bg-info': form.tax_policy === 'included',
            'bg-warning text-dark': form.tax_policy === 'excluded'
          }"
        >
          {{ form.tax_policy === 'no_tax'? $t('No Tax')
            : form.tax_policy === 'included'? $t('VAT Included')
            : $t('VAT Excluded') }}
        </span>
        <span
          v-if="form.tax_policy !== 'no_tax'"
          class="ms-2 text-muted"
          style="font-size: 12px;"
        >
          ({{ (vatRate * 100).toFixed(0) }}%)
        </span>
      </div>

      <!-- Section: Information -->
      <div class="card border-0 rounded-0 shadow-sm mx-0 mb-0">
        <div class="card-header bg-white border-bottom py-2 px-4">
          <span class="fw-semibold" style="font-size: 13px;">
            <i class="fa-solid fa-circle-info me-2 text-primary" />
            {{ $t('Goods Receipt Information') }}
          </span>
        </div>
        <div class="card-body px-4 py-3">
          <div class="row g-3">
            <div class="col-md-3">
              <label for="transaction_date" class="form-label fw-medium" style="font-size: 12px;">
                {{ $t('Transaction Date') }}
                <span class="text-danger">*</span>
              </label>
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-light border-end-0">
                  <i class="fa-solid fa-calendar-days text-muted" style="font-size: 11px;" />
                </span>
                <input
                  id="transaction_date"
                  type="date"
                  v-model.trim="v$.form.transaction_date.$model"
                  class="form-control shadow-none border-start-0"
                  :class="v$.form.transaction_date.$error? 'is-invalid': ''"
                  :placeholder="$t('Enter transaction date') + '...'"
                  :disabled="type === 'EDIT'"
                >
                <div v-for="error of v$.form.transaction_date.$errors"
                  class="invalid-feedback"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <label for="supplier" class="form-label fw-medium" style="font-size: 12px;">
                {{ $t('Supplier') }}
                <span class="text-danger">*</span>
              </label>
              <div class="input-group input-group-sm">
                <select2
                  style="width: 100%;"
                  v-model="v$.form.supplier_id.$model"
                  @select="selectSupplier"
                  :options="select2Suppliers.options"
                  :settings="select2Suppliers.setting"
                  :placeholder="$t('Enter Supplier') + '...'"
                  :disabled="type === 'EDIT'"
                />
                <div v-for="error of v$.form.supplier_id.$errors"
                  class="invalid-feedback"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <label for="branch" class="form-label fw-medium" style="font-size: 12px;">
                {{ $t('Branch') }}
                <span class="text-danger">*</span>
              </label>
              <div class="input-group input-group-sm">
                <select2
                  style="width: 100%;"
                  v-model="v$.form.branch_id.$model"
                  :options="select2Branches.options"
                  :settings="select2Branches.setting"
                  :placeholder="$t('Enter Branch') + '...'"
                  :disabled="type === 'EDIT'"
                />
                <div v-for="error of v$.form.branch_id.$errors"
                  class="invalid-feedback"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <!--
                ร้านที่ไม่จด VAT เครดิตภาษีซื้อไม่ได้ เลขนี้จึงไม่ใช่เรื่องภาษี (vat-compliance.md A1)
                แต่ยังเก็บไว้เป็นเลขอ้างอิงเอกสารผู้ขาย — เปลี่ยนแค่ป้าย ไม่ซ่อนช่อง
              -->
              <label for="ref_no" class="form-label fw-medium" style="font-size: 12px;">
                {{ needsTaxInvoice ? $t('Tax Invoice No.') : $t('Supplier Document No.') }}
                <span v-if="needsTaxInvoice" class="text-danger">*</span>
              </label>
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-light border-end-0">
                  <i class="fa-solid fa-hashtag text-muted" style="font-size: 11px;" />
                </span>
                <input
                  id="ref_no"
                  type="text"
                  v-model.trim="v$.form.ref_no.$model"
                  class="form-control shadow-none border-start-0"
                  :placeholder="$t('Number printed on the supplier document') + '...'"
                >
                <div v-for="error of v$.form.ref_no.$errors"
                  class="d-block invalid-feedback"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </div>
            </div>
            <!--
              วันที่บนใบกำกับของผู้ขาย — คนละตัวกับวันที่รับของด้านบน สองวันนี้ข้ามเดือนกันได้
              และรายงานภาษีซื้อยึดวันที่นี้เป็นเกณฑ์เดือนที่ยื่น (vat-compliance.md ตัดสินใจ B1)
            -->
            <div class="col-md-3" v-if="needsTaxInvoice">
              <label for="supplier_invoice_date" class="form-label fw-medium" style="font-size: 12px;">
                {{ $t('Tax Invoice Date') }}
                <span class="text-danger">*</span>
              </label>
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-light border-end-0">
                  <i class="fa-regular fa-calendar text-muted" style="font-size: 11px;" />
                </span>
                <input
                  id="supplier_invoice_date"
                  type="date"
                  v-model="v$.form.supplier_invoice_date.$model"
                  class="form-control shadow-none border-start-0"
                >
                <div v-for="error of v$.form.supplier_invoice_date.$errors"
                  class="d-block invalid-feedback"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section: Items -->
      <div class="card border-0 rounded-0 shadow-sm mx-0 mb-0 mt-2">
        <div class="card-header bg-white border-bottom py-2 px-4 d-flex justify-content-between align-items-center">
          <span class="fw-semibold" style="font-size: 13px;">
            <i class="fa-solid fa-basket-shopping me-2 text-primary" />
            {{ $t('Items') }}
            <span v-if="form.items.length > 0" class="badge bg-primary rounded-pill ms-1">
              {{ form.items.length }}
            </span>
          </span>
        </div>
        <div class="card-body p-0">
          <div v-if="form.items.length > 0" class="table-responsive">
            <table class="w-100" style="border-collapse: collapse; min-width: 700px;">
              <thead>
                <tr style="border-bottom: 2px solid #e9ecef;">
                  <th class="text-center text-muted fw-medium py-2 px-2" style="font-size: 13px; width: 30px;">#</th>
                  <th class="text-start text-muted fw-medium py-2 px-2" style="font-size: 13px; min-width: 140px;">{{ $t('Inventory') }}</th>
                  <th class="text-end text-muted fw-medium py-2 px-2" style="font-size: 13px; min-width: 80px; width: 10%;">{{ $t('Quantity') }}</th>
                  <th class="text-start text-muted fw-medium py-2 px-2" style="font-size: 13px; min-width: 90px; width: 12%;">{{ $t('Unit') }}</th>
                  <th class="text-end text-muted fw-medium py-2 px-2" style="font-size: 13px; min-width: 80px; width: 10%;">{{ $t('Price') }}</th>
                  <th class="text-end text-muted fw-medium py-2 px-2" style="font-size: 13px; min-width: 80px; width: 10%;">{{ $t('Discount') }}</th>
                  <th class="text-start text-muted fw-medium py-2 px-2" style="font-size: 13px; min-width: 110px; width: 13%;">{{ $t('Expiry Date') }}</th>
                  <th class="text-end text-muted fw-medium py-2 px-2" style="font-size: 13px; min-width: 90px; width: 12%;">{{ $t('Total') }}</th>
                  <th style="width: 30px;" />
                </tr>
              </thead>
              <tbody>
                <item-form
                  v-for="(item, index) in form.items"
                  :key="index"
                  :index="index"
                  :item="item"
                  :tax-policy="form.tax_policy"
                  :vat-rate="vatRate"
                  :discount="form.discount"
                  :select2-unit-options="optionsUnitItems[index]"
                  @calculate-discount="calculateProratedOrderDiscount()"
                />
              </tbody>
            </table>
          </div>
          <div v-else class="mx-3 my-3 p-3 rounded-3 text-center">
            <i class="fa-solid fa-box-open fa-2x mb-2 opacity-50" />
            <div class="fw-medium text-muted mb-2">
              {{ $t('No Available Data') }}
            </div>
          </div>
          <!-- Add Items -->
          <div
            v-if="type === 'CREATE'"
            class="mx-3 my-3 p-3 rounded-3 text-center"
            style="border: 2px dashed #c5ccd6; background: #fafbfc;"
          >
            <i class="fa-solid fa-plus-circle text-primary mb-2" style="font-size: 20px;" />
            <div class="fw-medium text-muted mb-2" style="font-size: 13px;">
              {{ $t('Add Items') }}
            </div>
            <div style="max-width: 350px; margin: 0 auto;">
              <select2
                v-model="selectInventory"
                @select="onSelectedInventory"
                :options="select2Items.options"
                :settings="select2Items.setting"
                :placeholder="$t('search') + '...'"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Section: Summary -->
      <div class="card border-0 rounded-0 shadow-sm mx-0 mb-0 mt-2">
        <div class="card-header bg-white border-bottom py-2 px-4">
          <span class="fw-semibold" style="font-size: 13px;">
            <i class="fa-solid fa-calculator me-2 text-primary" />
            {{ $t('Summary') }}
          </span>
        </div>
        <div class="card-body px-4 py-3">
          <div class="row g-3">
            <!-- Note -->
            <div class="col-md-6">
              <label for="note" class="form-label fw-medium" style="font-size: 12px;">
                <i class="fa-solid fa-note-sticky me-1 text-muted" />
                {{ $t('note') }}
              </label>
              <textarea
                class="form-control shadow-none form-control-sm"
                id="note"
                rows="5"
                style="font-size: 13px; resize: none;"
                v-model.trim="v$.form.note.$model"
                :placeholder="$t('note') + '...'"
              />
            </div>
            <!-- Totals -->
            <div class="col-md-6">
              <div class="rounded-3 p-3" style="background: #f8f9fc; border: 1px solid #e9ecef;">
                <table class="w-100" style="font-size: 13px;">
                  <tbody>
                    <!-- Sub Total -->
                    <tr>
                      <td class="pb-2 text-muted">{{ $t('Sub Total') }}</td>
                      <td class="text-end pb-2 fw-medium">
                        {{ formatNumberTwoDigit(subTotalBeforeVat? subTotalBeforeVat: '0.00') }}
                      </td>
                    </tr>

                    <!-- Shipping -->
                    <tr>
                      <td class="pb-2 text-muted">
                        <label for="shipping" class="form-label mb-0">{{ $t('Shipping') }}</label>
                      </td>
                      <td class="text-end pb-2" style="width: 55%;">
                        <div class="input-group input-group-sm">
                          <span class="input-group-text bg-light" style="font-size: 12px;">฿</span>
                          <input
                            id="shipping"
                            v-model.number="v$.form.shipping_price.$model"
                            type="number"
                            inputmode="decimal"
                            class="form-control shadow-none text-end"
                            style="font-size: 13px;"
                            placeholder="0.00"
                            :disabled="type === 'EDIT'"
                          >
                        </div>
                      </td>
                    </tr>

                    <!-- Discount -->
                    <tr>
                      <td class="pb-2 text-muted">
                        <label for="discount" class="form-label mb-0">{{ $t('Discount') }}</label>
                      </td>
                      <td class="text-end pb-2">
                        <div class="input-group input-group-sm">
                          <span class="input-group-text bg-light" style="font-size: 12px;">฿</span>
                          <input
                            id="discount"
                            v-model.number="v$.form.discount.$model"
                            type="number"
                            inputmode="decimal"
                            class="form-control shadow-none text-end"
                            style="font-size: 13px;"
                            placeholder="0.00"
                            :disabled="type === 'EDIT'"
                          >
                        </div>
                      </td>
                    </tr>

                    <!-- Total Before VAT & VAT -->
                    <tr v-if="form.tax_policy !== 'no_tax'">
                      <td colspan="2"><hr class="my-1" style="border-color: #dee2e6;" /></td>
                    </tr>
                    <tr v-if="form.tax_policy !== 'no_tax'">
                      <td class="pb-1 pt-1" style="font-size: 12px;">{{ $t('Total Before VAT') }}</td>
                      <td class="text-end pb-1 pt-1" style="font-size: 12px;">
                        {{ formatNumberTwoDigit(form.total_before_vat) }}
                      </td>
                    </tr>
                    <tr v-if="form.tax_policy !== 'no_tax'">
                      <td class="pb-2" style="font-size: 12px;">{{ $t('Total VAT') }}</td>
                      <td class="text-end pb-2" style="font-size: 12px;">
                        {{ formatNumberTwoDigit(form.total_vat) }}
                      </td>
                    </tr>

                    <!-- Grand Total -->
                    <tr>
                      <td colspan="2"><hr class="my-1" style="border-color: #adb5bd;" /></td>
                    </tr>
                    <tr>
                      <td class="pt-2">
                        <b style="font-size: 15px;">{{ $t('Grand Total') }}</b>
                      </td>
                      <td class="text-end pt-2">
                        <b style="font-size: 15px; color: #4a6cf7;">
                          {{ total? formatNumberTwoDigit(total): '0.00' }}
                        </b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="border-top d-flex justify-content-center align-items-center py-3 px-4"
      style="background: #f8f9fc;"
      v-if="!loading"
    >
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="btn btn-sm btn-outline-secondary ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { GoodsReceipts } from '@/services/endpoint/goods-receipts.service';
import { AUTH, API_URLS } from '@/services/endpoint/_constants';
import Select2 from 'vue3-select2-component';
import moment from "moment";
import signature  from '@/services/signature';
import ItemForm from './ItemForm.vue';
import numeral from 'numeral';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    // ส่งเป็น getter เพราะเงื่อนไข "ต้องมีใบกำกับไหม" ขึ้นกับสาขาที่เลือก ไม่ได้อยู่ใน form
    return validation(this.form, () => this.needsTaxInvoice)
  },
  props: {
    type: {
      type: String,
      default: 'CREATE'
    },
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    let vm = this;
    return {
      selectInventory: [],
      listItems: [],
      loading: true,
      loadingSave: false,
      hasPermission: false,
      select2Suppliers: {
        options: [],
        setting: {
          minimumResultsForSearch: true,
          multiple: false,
          language: {
            maximumSelected: function (e) {
              return vm.$t('You can only select') + ' ' + e.maximum + ' ' + vm.$t('items');
            },
            loadingMore: function (e) {
              return '<i class="fa-solid fa-spinner fa-spin" />'
            }
          },
          ajax: {
            beforeSend: function (request) {
              request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN);
            },
            url: AUTH.URL + API_URLS.API_GOODS_RECCEIPT_SUPPLIER,
            data: function (params) {
              var query = {
                search: params.term,
                page: params.page || 1,
                limit: 10
              };
              return signature($.param(query)).substring(1);
            },
            processResults: function (data, params) {
              let option = data.data;
              let pageCurrent = data.meta.current_page;
              option = option.map(function(item) {
                return {
                  id: item.id,
                  // ผู้ขายรายเดียวมีหลายสาขา แต่ละสาขาออกใบกำกับคนละชุด
                  // ต้องเห็นสาขาในตัวเลือก ไม่งั้นเลือกผิดสาขาแล้วเลขใบกำกับไม่ตรงกับใบที่ถืออยู่
                  text: item.branch ? item.fullname + ' — ' + item.branch : item.fullname,
                  tax_policy: item.tax_policy
                }
              });
              return {
                results: option,
                pagination: {
                  more: (pageCurrent * 10) < data.meta.total
                }
              }
            }
          }
        }
      },
      vatRate: 0.07,
      select2Items: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
          multiple: true,
          language: {
            maximumSelected: function (e) {
              return vm.$t('You can only select') + ' ' + e.maximum + ' ' + vm.$t('items');
            },
            loadingMore: function (e) {
              return '<i class="fa-solid fa-spinner fa-spin" />'
            }
          },
          ajax: {
            beforeSend: function (request) {
              request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN);
            },
            url: AUTH.URL + API_URLS.API_GOODS_RECCEIPT_ITEM,
            data: function (params) {
              var query = {
                search: params.term,
                page: params.page || 1,
                limit: 10
              };
              return signature($.param(query)).substring(1);
            },
            processResults: function (data, params) {
              let option = data.data;
              let pageCurrent = data.meta.current_page;
              option = option.map(function(item) {
                return {
                  id: item.id,
                  text: item.name,
                  tax_status: item.tax_status,
                  cost: item.cost,
                  unit_base: item.unit_base,
                  unit_conversions: item.unit_conversions
                  // disabled: vm.form.items.find((inventory) => inventory.inventory_id == item.id)? true: false
                }
              });
              return {
                results: option,
                pagination: {
                  more: (pageCurrent * 10) < data.meta.total
                }
              }
            }
          }
        }
      },
      select2Branches: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
        }
      },
      optionsUnitItems: [],
      // ค่าภาษีเป็นของร้าน ไม่ใช่ของสาขา (api/docs/vat-compliance.md §2) — มาจาก filter.shop_tax
      shopTax: {
        tax_policy: 'no_tax',
        vat_rate: 0,
      },
      form: {
        transaction_date: moment(Date.now()).format('YYYY-MM-DD'),
        supplier_id: '',
        branch_id: 1,
        tax_policy: 'no_tax',
        // ref_no = เลขที่ใบกำกับของผู้ขาย · supplier_invoice_date = วันที่บนใบกำกับใบนั้น
        // (คนละตัวกับ transaction_date ที่เป็นวันรับของ — vat-compliance.md B1)
        ref_no: '',
        supplier_invoice_date: '',
        items: [],
        quantity: '',
        sub_total: '',
        discount_before_vat: '',
        discount_vat: '',
        discount: '',
        shipping_price_before_vat: '',
        shipping_price_vat: '',
        shipping_price: '',
        total_before_vat: '',
        total_vat: '',
        total: '',
        note: ''
      },
    }
  },
  components: {
    PlaceholderContent,
    AccessDenied,
    Ladda,
    Select2,
    ItemForm
  },
  methods: {
    selectSupplier(value) {
      this.form.tax_policy = value.tax_policy;
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    async deleteItem(index) {
      this.form.items.splice(index, 1);
      this.optionsUnitItems.splice(index, 1);
      this.select2Items.options = [];
    },
    async onSelectedInventory(e) {
      let vm = this;
      this.selectInventory = [];
      if (!e || !e.id) return;
      // if (this.form.items.find((item) => item.inventory_id == e.id)) return;
      this.form.items.push({
        inventory_id: e.id,
        name: e.text,
        unit_id: e.unit_base.id,
        tax_status: e.tax_status,
        // MAC ต่อหน่วยฐาน + ชื่อหน่วยฐาน ไว้เทียบดักราคา/หน่วยผิด (ตัดทิ้งก่อนส่ง API)
        reference_cost: parseFloat(e.cost) || 0,
        base_unit_name: e.unit_base.name || '',
        inventory_unit_id: '',
        ordered_quantity: '',
        conversion_factor: 1,
        price_before_vat: 0,
        price_vat: 0,
        price: '',
        discount_before_vat: 0,
        discount_vat: 0,
        discount: '',
        prorated_order_discount_before_vat: 0,
        prorated_order_discount_vat: 0,
        prorated_order_discount: '',
        prorated_shipping_cost_before_vat: 0,
        prorated_shipping_cost_vat: 0,
        prorated_shipping_cost: '',
        total_before_vat: 0,
        total_vat: 0,
        total: 0,
        expiry_date: ''
      });
      this.optionsUnitItems.push([...[{
        id: e.unit_base.id,
        inventory_unit_id: '',
        text: vm.$t(e.unit_base.name),
        conversion_factor: 1
      }], ...e.unit_conversions.map(function(unit) {
        return {
          id: unit.unit_id,
          inventory_unit_id: unit.id,
          text: vm.$t(unit.name) + ' ('+ vm.formatNumberTwoDigit(unit.conversion_factor) + ' ' + vm.$t(e.unit_base.name) + ')',
          conversion_factor: unit.conversion_factor
        }
      })]);
      this.select2Items.options = [];
    },
    async getCreateData() {
      await GoodsReceipts.getCreate().then(res => {
        let vm = this;
        this.shopTax = res.data.filter?.shop_tax || this.shopTax;
        this.select2Branches.options = res.data.data.branches.map(function(item) {
          return {
            id: item.id,
            text: vm.$t(item.name)
          }
        });
        this.form.branch_id = res.data.data.branches[0].id || '';
        this.vatRate = (this.shopTax.vat_rate || 7) / 100;
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    async getUpdateData() {
      await GoodsReceipts.getUpdate(this.id).then(res => {
        let vm = this;
        this.shopTax = res.data.filter?.shop_tax || this.shopTax;
        this.vatRate = (this.shopTax.vat_rate || 7) / 100;
        this.select2Branches.options = res.data.filter.branches.map(function(item) {
          return {
            id: item.id,
            text: vm.$t(item.name)
          }
        });
        this.select2Suppliers.options = [
          {
            id: res.data.data.supplier_id,
            text: this.$t(res.data.data.supplier)
          }
        ];

        this.form = {
          transaction_date: moment(res.data.data.transaction_date).format('YYYY-MM-DD'),
          supplier_id: res.data.data.supplier_id,
          branch_id: res.data.data.branch_id,
          tax_policy: res.data.data.tax_policy,
          ref_no: res.data.data.ref_no,
          supplier_invoice_date: res.data.data.supplier_invoice_date || '',
          items: res.data.data.items.map(function(item) {
            vm.optionsUnitItems.push([{
              id: item.unit_id,
              inventory_unit_id: '',
              text: vm.$t(item.unit),
              conversion_factor: 1
            }]);
            return {
              inventory_id: 0,
              name: item.name,
              lot: item.lot_number,
              unit_id: item.unit_id,
              ordered_quantity: item.ordered_quantity != '0.0000'? item.ordered_quantity*1: '',
              conversion_factor: item.conversion_factor != '0.0000'? item.conversion_factor*1: '',
              price: item.price != '0.0000'? item.price*1: '',
              discount: item.discount != '0.0000'? item.discount*1: '',
              tax_status: item.tax_status,
              total_before_vat: item.total_before_vat != '0.0000'? item.total_before_vat*1: '',
              total_vat: item.total_vat != '0.0000'? item.total_vat*1: '',
              total: item.total != '0.0000'? item.total*1: '',
              expiry_date: moment(res.data.data.expiry_date).format('YYYY-MM-DD')
            }
          }),
          quantity: res.data.data.quantity != '0.0000'? res.data.data.quantity*1: '',
          sub_total: res.data.data.sub_total != '0.0000'? res.data.data.sub_total*1: '',
          discount_before_vat: res.data.data.discount_before_vat != '0.0000'? res.data.data.discount_before_vat*1: '',
          discount_vat: res.data.data.discount_vat != '0.0000'? res.data.data.discount_vat*1: '',
          discount: res.data.data.discount != '0.0000'? res.data.data.discount*1: '',
          shipping_price_before_vat: res.data.data.shipping_price_before_vat != '0.0000'? res.data.data.shipping_price_before_vat*1: '',
          shipping_price_vat: res.data.data.shipping_price_vat != '0.0000'? res.data.data.shipping_price_vat*1: '',
          shipping_price: res.data.data.shipping_price != '0.0000'? res.data.data.shipping_price*1: '',
          total_before_vat: res.data.data.total_before_vat != '0.0000'? res.data.data.total_before_vat*1: '',
          total_vat: res.data.data.total_vat != '0.0000'? res.data.data.total_vat*1: '',
          total: res.data.data.total != '0.0000'? res.data.data.total*1: '',
          note: res.data.data.note,
        };
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
			  throw err;
      });
    },
    setParamsBeforeSendCreate() {
      let params = _.cloneDeep(this.form);
      // reference_cost/base_unit_name เป็น state ฝั่งฟอร์ม (ไว้ดักราคาผิดปกติ) — ไม่ส่งไป BE
      (params.items || []).forEach((item) => { delete item.reference_cost; delete item.base_unit_name; });
      return $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    // ดักลงหน่วย/ราคาผิด (เช่น ซื้อเป็นถุงแต่ลงราคาต่อชิ้น): เทียบราคาต่อหน่วยฐานกับ MAC เดิม
    // เบี่ยงเกิน 3 เท่า (ขึ้นหรือลง) → ให้ยืนยันก่อนบันทึก — ราคาวัตถุดิบปกติไม่แกว่งระดับนี้
    // แต่พลาดหน่วย (×conversion_factor) แกว่งเป็นสิบเท่า
    async confirmAbnormalCosts() {
      const THRESHOLD = 3;
      const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const suspicious = [];
      this.form.items.forEach((item) => {
        const ref = Number(item.reference_cost) || 0;
        const price = Number(item.price) || 0;
        const factor = Number(item.conversion_factor) || 0;
        if (ref <= 0 || price <= 0 || factor <= 0) return;
        const costPerBase = price / factor;
        const ratio = costPerBase / ref;
        if (ratio >= THRESHOLD || ratio <= 1 / THRESHOLD) {
          const unit = this.$t(item.base_unit_name || '');
          const direction = ratio >= 1
            ? this.$t('higher than before') + ' ~' + this.formatNumber(ratio) + ' ' + this.$t('times')
            : this.$t('lower than before') + ' ~' + this.formatNumber(1 / ratio) + ' ' + this.$t('times');
          suspicious.push(
            '<div class="mb-2 text-start">'
            + '<b>' + esc(item.name) + '</b><br>'
            + esc(this.$t('Entered price')) + ': <b>' + this.formatNumberTwoDigit(costPerBase) + '</b> / ' + esc(unit)
            + ' &nbsp;·&nbsp; ' + esc(this.$t('previous cost')) + ': ' + this.formatNumberTwoDigit(ref) + ' / ' + esc(unit) + '<br>'
            + '<span class="text-danger fw-semibold">' + esc(direction) + '</span>'
            + '</div>'
          );
        }
      });
      if (!suspicious.length) return true;
      const result = await this.$swal({
        icon: "warning",
        iconColor: "#FF7043",
        title: this.$t('Price per unit looks unusual'),
        html: suspicious.join('')
          + '<div class="text-secondary text-start" style="font-size: 13px;">'
          + esc(this.$t('Please check that the selected unit matches the price you entered (e.g. price per bag requires the bag unit).'))
          + '</div>',
        showCancelButton: true,
        cancelButtonText: this.$t('Back to edit'),
        confirmButtonText: '<i class="fas fa-check-circle me-1"></i>' + this.$t('Confirm'),
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-primary me-2",
          cancelButton: "btn btn-danger",
        },
        focusConfirm: false,
      });
      return result.isConfirmed;
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      let params = {
        ref_no: this.form.ref_no,
        note: this.form.note
      }
      // ใบเก่าที่รับก่อนมีฟิลด์นี้ ค่อยมาเติมวันที่ใบกำกับย้อนหลังได้ (ไม่บังคับ)
      if (this.form.supplier_invoice_date) {
        params.supplier_invoice_date = this.form.supplier_invoice_date;
      }
      await GoodsReceipts.update(this.id, params).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Goods Receipt!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        // API มี error 2 ทรง: `errors` รายฟิลด์ กับ `message` ก้อนเดียว
        // เดิมจับแต่ทรงแรก ทรงที่สอง (ValidationException / ErrorException) จึงเงียบสนิท
        // คนคีย์กดบันทึกแล้วไม่มีอะไรเกิดขึ้น ไม่รู้ว่าพลาดตรงไหน
        if (err?.response?.data?.errors) {
          this.errors = err.response.data.errors;
        }
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err.response.data.message));
        } else if (!err?.response?.data?.errors) {
          this.$toast.error(this.$t('Failed'));
        }
        this.loadingSave = false;
      });
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      if (!(await this.confirmAbnormalCosts())) {
        this.loadingSave = false;
        return;
      }
      await GoodsReceipts.create(this.setParamsBeforeSendCreate()).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Goods Receipt!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        // API มี error 2 ทรง: `errors` รายฟิลด์ กับ `message` ก้อนเดียว
        // เดิมจับแต่ทรงแรก ทรงที่สอง (ValidationException / ErrorException) จึงเงียบสนิท
        // คนคีย์กดบันทึกแล้วไม่มีอะไรเกิดขึ้น ไม่รู้ว่าพลาดตรงไหน
        if (err?.response?.data?.errors) {
          this.errors = err.response.data.errors;
        }
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err.response.data.message));
        } else if (!err?.response?.data?.errors) {
          this.$toast.error(this.$t('Failed'));
        }
        this.loadingSave = false;
      });
    },
    recalculateAllVat() {
      const discount = Number(this.form.discount) || 0;
      const shipping = Number(this.form.shipping_price) || 0;
      if (this.form.tax_policy === 'included') {
        this.form.discount_before_vat = (discount / (1 + this.vatRate)).toFixed(4);
        this.form.discount_vat = (Number(this.form.discount_before_vat) * this.vatRate).toFixed(4);
        this.form.shipping_price_before_vat = (shipping / (1 + this.vatRate)).toFixed(4);
        this.form.shipping_price_vat = (Number(this.form.shipping_price_before_vat) * this.vatRate).toFixed(4);
      } else if (this.form.tax_policy === 'excluded') {
        this.form.discount_before_vat = discount.toFixed(4);
        this.form.discount_vat = (discount * this.vatRate).toFixed(4);
        this.form.shipping_price_before_vat = shipping.toFixed(4);
        this.form.shipping_price_vat = (shipping * this.vatRate).toFixed(4);
      } else {
        this.form.discount_before_vat = discount.toFixed(4);
        this.form.discount_vat = (0).toFixed(4);
        this.form.shipping_price_before_vat = shipping.toFixed(4);
        this.form.shipping_price_vat = (0).toFixed(4);
      }
      this.calculateProratedOrderDiscount();
      this.calculateProratedShippingCost();
    },
    calculateProratedOrderDiscount() {
      if (this.subTotalBeforeVat <= 0) return;
      const percentDiscount = Number(this.form.discount || 0) / this.subTotalBeforeVat;
      let discountBeforeVat = 0;
      let discountVat = 0;
      this.form.items.forEach(item => {
        const proratedOrderDiscount = Number(item.total_before_vat) * percentDiscount;
        item.prorated_order_discount = proratedOrderDiscount.toFixed(4);
        if (this.form.tax_policy === 'included' && item.tax_status == 'vatable') {
          item.prorated_order_discount_before_vat = (proratedOrderDiscount / (1 + this.vatRate)).toFixed(4);
          item.prorated_order_discount_vat = (Number(item.prorated_order_discount_before_vat) * this.vatRate).toFixed(4);
        } else if (this.form.tax_policy === 'excluded' && item.tax_status == 'vatable') {
          item.prorated_order_discount_before_vat = proratedOrderDiscount.toFixed(4);
          item.prorated_order_discount_vat = (proratedOrderDiscount * this.vatRate).toFixed(4);
        } else {
          item.prorated_order_discount_before_vat = proratedOrderDiscount.toFixed(4);
          item.prorated_order_discount_vat = (0).toFixed(4);
        }
        discountBeforeVat += Number(item.prorated_order_discount_before_vat);
        discountVat += Number(item.prorated_order_discount_vat);
      });

      this.form.discount_before_vat = discountBeforeVat.toFixed(4);
      this.form.discount_vat = discountVat.toFixed(4);
    },
    calculateProratedShippingCost() {
      if (!this.subTotalBeforeVat || this.subTotalBeforeVat <= 0) {
        this.form.shipping_price_before_vat = (Number(this.form.shipping_price) || 0).toFixed(4);
        this.form.shipping_price_vat = (0).toFixed(4);
        return;
      }
      const totalShippingPrice = Number(this.form.shipping_price) || 0;
      let shippingPriceBeforeVat = 0;
      let shippingPriceVat = 0;
      this.form.items.forEach(item => {
        const itemRatio = Number(item.total_before_vat) / this.subTotalBeforeVat;
        const proratedShippingCost = itemRatio * totalShippingPrice;
        item.prorated_shipping_cost = proratedShippingCost.toFixed(4);
        if (this.form.tax_policy === 'included' && item.tax_status === 'vatable') {
          item.prorated_shipping_cost_before_vat = (proratedShippingCost / (1 + this.vatRate)).toFixed(4);
          item.prorated_shipping_cost_vat = (Number(item.prorated_shipping_cost_before_vat) * this.vatRate).toFixed(4);
        } else if (this.form.tax_policy === 'excluded' && item.tax_status === 'vatable') {
          item.prorated_shipping_cost_before_vat = proratedShippingCost.toFixed(4);
          item.prorated_shipping_cost_vat = (proratedShippingCost * this.vatRate).toFixed(4);
        } else {
          item.prorated_shipping_cost_before_vat = proratedShippingCost.toFixed(4);
          item.prorated_shipping_cost_vat = (0).toFixed(4);
        }
        shippingPriceBeforeVat += Number(item.prorated_shipping_cost_before_vat);
        shippingPriceVat += Number(item.prorated_shipping_cost_vat);
      });
      this.form.shipping_price_before_vat = shippingPriceBeforeVat.toFixed(4);
      this.form.shipping_price_vat = shippingPriceVat.toFixed(4);
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas',async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
    this.select2Suppliers.setting.dropdownParent = this.$refs.offcanvas;
  },
  computed: {
    // ใบซื้อที่จะเข้ารายงานภาษีซื้อ = ใบมี VAT และร้านจด VAT
    // เฉพาะใบพวกนี้ที่บังคับกรอกเลขที่/วันที่ใบกำกับของผู้ขาย
    // (silver-spoon-api/docs/vat-compliance.md ตัดสินใจ A1)
    needsTaxInvoice() {
      if (this.form.tax_policy === 'no_tax') return false;

      return !!this.shopTax.tax_policy && this.shopTax.tax_policy !== 'no_tax';
    },
    subTotalBeforeVat() {
      return this.form.items.reduce((sum, value) => {
        return sum + (Number(value.total_before_vat) || 0);
      }, 0);
    },
    subTotalVat() {
      return this.form.items.reduce((sum, value) => {
        return sum + (Number(value.total_vat) || 0);
      }, 0);
    },
    subTotal() {
      return this.form.items.reduce((sum, value) => {
        return sum + (Number(value.total) || 0);
      }, 0);
    },
    totalQuantity() {
      return this.form.items.reduce((sum, value) => {
        return sum + (Number(value.ordered_quantity) || 0);
      }, 0);
    },
    total() {
      const shipping = Number(this.form.shipping_price) || 0;
      const discount = Number(this.form.discount) || 0;
      if (this.form.tax_policy === 'excluded') {
        return this.subTotal + shipping * (1 + this.vatRate) - discount * (1 + this.vatRate);
      }
      return this.subTotal + shipping - discount;
    }
  },
  watch: {
    subTotal: {
      handler(nval) {
        this.form.sub_total = nval;
        this.calculateProratedOrderDiscount();
        this.calculateProratedShippingCost();
      },
      immediate: true
    },
    totalQuantity: {
      handler(nval) {
        this.form.quantity = nval;
      },
      immediate: true
    },
    'form.discount'(nval) {
      this.calculateProratedOrderDiscount();
    },
    'form.shipping_price'(nval) {
      this.calculateProratedShippingCost();
    },
    total: {
      handler(nval) {// 1. หายอด VAT รวมเสมอ (สูตรนี้ใช้ได้กับทุก Policy)
        this.form.total_vat = (
          Number(this.subTotalVat)
          + Number(this.form.shipping_price_vat || 0)
          - Number(this.form.discount_vat || 0)
        ).toFixed(4);

        // 2. แยกสายการคำนวณตาม Policy
        if (this.form.tax_policy === 'excluded') {
          // 1. ล็อกยอด Before VAT ด้วย "ตัวเลขดิบ" เพื่อทิ้งเศษทศนิยม
          // (620 + 60 - 100 = 580.0000 ถ้วน!)
          this.form.total_before_vat = (
            Number(this.subTotalBeforeVat)
            + Number(this.form.shipping_price || 0)
            - Number(this.form.discount || 0)
          ).toFixed(4);

          // 2. ยอด Grand Total เกิดจากการเอา Before VAT + VAT รวม
          // (580.0000 + 27.4010 = 607.4010 เป๊ะ!)
          this.form.total = (
            Number(this.form.total_before_vat) + Number(this.form.total_vat)
          ).toFixed(4);
        } else {
          // Included / No Tax: ตัวเลขที่คีย์มาคือ Gross -> ต้องล็อคยอด Grand Total ก่อน
          this.form.total = (
            Number(this.subTotal)
            + Number(this.form.shipping_price || 0)
            - Number(this.form.discount || 0)
          ).toFixed(4);

          // Before VAT เกิดจากการเอา Grand Total ถอด VAT ออก (กันเศษทศนิยมดิฟ)
          this.form.total_before_vat = (
            Number(this.form.total) - Number(this.form.total_vat)
          ).toFixed(4);
        }
      },
      immediate: true
    },
    "form.tax_policy"() {
      this.recalculateAllVat();
    },
    vatRate() {
      this.recalculateAllVat();
    }
  }
}
</script>
